import React from 'react';
import { Timer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';

/** Seconds as HH:MM:SS, or MM:SS under an hour. */
export function formatExamTime(seconds: number): string {
    const totalMins = Math.floor(seconds / 60);
    const mins = totalMins % 60;
    const hrs = Math.floor(totalMins / 60);
    const secs = seconds % 60;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return hrs > 0 ? `${pad(hrs)}:${pad(mins)}:${pad(secs)}` : `${pad(mins)}:${pad(secs)}`;
}

export type ExamTimerTone = 'normal' | 'warn' | 'danger';

/** What the runner's clock shows right now; `null` hides the chip. */
export interface ExamClock {
    tone: ExamTimerTone;
    seconds: number;
    /** The per-question clock just ran out ("time's up" flash). */
    timeUp: boolean;
    /** Per-question pacing: the chip shows the question clock, not the test budget. */
    perQuestion: boolean;
}

export interface ExamClockInput {
    /** Start-page toggle; off hides the clock entirely. */
    showTimer: boolean;
    perQuestionMode: boolean;
    questionTimeRemaining: number | null;
    timeUpFlash: boolean;
    timeRemaining: number | null;
    /** Under 5 minutes of the test budget left. */
    timerWarn: boolean;
    /** Under 1 minute of the test budget left. */
    timerDanger: boolean;
}

/** Display only: the countdown and auto-submit live in the runner. */
export function resolveExamClock(input: ExamClockInput): ExamClock | null {
    if (!input.showTimer) return null;
    // Per-question mode shows the question clock (the aggregate budget still
    // runs invisibly as the server-enforced safety net).
    if (input.perQuestionMode && input.questionTimeRemaining !== null) {
        return {
            tone: input.timeUpFlash || input.questionTimeRemaining <= 10 ? 'danger' : 'normal',
            seconds: input.questionTimeRemaining,
            timeUp: input.timeUpFlash,
            perQuestion: true,
        };
    }
    if (input.timeRemaining === null) return null;
    return {
        tone: input.timerDanger ? 'danger' : input.timerWarn ? 'warn' : 'normal',
        seconds: input.timeRemaining,
        timeUp: false,
        perQuestion: false,
    };
}

const TONES: Record<ExamTimerTone, string> = {
    normal: 'border-border bg-card text-foreground',
    warn: 'border-warning-200 bg-warning-50 text-warning-700 dark:border-warning-900/60 dark:bg-warning-900/30 dark:text-warning-400',
    danger: 'border-error-200 bg-error-50 text-error-700 dark:border-error-900/60 dark:bg-error-900/30 dark:text-error-400',
};

export interface ExamClockChipProps {
    clock: ExamClock;
    /** 36px in the phone header, 40px in the desktop top bar. */
    size?: 'sm' | 'md';
    /** Spell out "per question" next to the time (the phone header keeps it in the name only). */
    showPerQuestionLabel?: boolean;
}

export function ExamClockChip({ clock, size = 'md', showPerQuestionLabel = true }: ExamClockChipProps) {
    const { t } = useTranslation();
    const perQuestionLabel = clock.perQuestion && !clock.timeUp ? t('time_per_question') : null;
    return (
        <span
            role="timer"
            aria-label={
                perQuestionLabel && !showPerQuestionLabel
                    ? `${t('monitor_counter_timeleft')} · ${perQuestionLabel}`
                    : t('monitor_counter_timeleft')
            }
            className={cn(
                'inline-flex shrink-0 items-center gap-2 rounded-md border px-3',
                size === 'sm' ? 'h-9' : 'h-10',
                TONES[clock.tone],
            )}
        >
            <Timer className="h-4 w-4 shrink-0" aria-hidden />
            <span className="whitespace-nowrap text-[15px] font-bold tracking-[0.01em] tabular-nums">
                {clock.timeUp ? t('time_is_up') : formatExamTime(clock.seconds)}
            </span>
            {perQuestionLabel && showPerQuestionLabel && (
                <span className="whitespace-nowrap text-[13px] font-medium">· {perQuestionLabel}</span>
            )}
        </span>
    );
}

export default ExamClockChip;
